import { format } from 'date-fns';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { usePopover } from '@adornis/functional-lit/use-popover.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { MicrosoftAPI } from '../../../microsoft-graph/api/MicrosoftAPI.js';
import { renderDashboardCardHeader, renderDashboardLoadingCard } from './StatRenderUtilities.js';

type MicrosoftErrorItem = {
  createdAt?: Maybe<Date>;
  message?: Maybe<string>;
  code?: Maybe<string>;
  url?: Maybe<string>;
  method?: Maybe<string>;
  data?: Maybe<string>;
};

/** One error row; the details open on click and stay open so long URLs and payloads can be read and scrolled. */
const MicrosoftErrorRow = stateful((props: { error: MicrosoftErrorItem }) => {
  const { error } = props;
  const details = usePopover({ positionArea: 'bottom', closeOnClickOutside: true });

  return html`
    <li>
      <button
        type="button"
        class="flex w-full min-w-0 items-start gap-3 rounded-field p-2 text-left hover:bg-base-200"
        style="anchor-name: ${details.anchorName};"
        ${ref(details.anchorRef)}
        @click=${() => {
          if (details.visible) details.hide();
          else details.show();
        }}
      >
        <span class="icon-[material-symbols--error-outline-rounded] mt-0.5 size-5 shrink-0 text-error"></span>
        <span class="flex min-w-0 flex-1 flex-col gap-1">
          <span class="flex flex-wrap items-center justify-between gap-2">
            <span class="badge badge-error">${error.code || 'Unbekannt'}</span>
            <span class="text-sm text-base-content/70">
              ${error.createdAt ? format(error.createdAt, 'dd.MM.yyyy HH:mm:ss') : ''}
            </span>
          </span>
          <span class="truncate text-sm font-semibold">${error.method} ${error.url}</span>
          <span class="truncate text-sm text-base-content/70">${error.message}</span>
        </span>
      </button>
      <div
        popover="manual"
        tabindex="-1"
        id=${details.popoverId}
        style="position-anchor: ${details.anchorName}; ${details.getAnchorStyles('position-try-fallbacks: flip-block;')}"
        class="max-h-96 overflow-y-auto rounded-box border border-base-300 bg-base-100 text-base-content shadow-2xl"
        ${ref(details.popoverRef)}
      >
        <dl class="grid max-w-md gap-x-3 gap-y-2 p-4 text-left text-sm sm:grid-cols-[max-content_1fr]">
          <dt class="font-semibold text-base-content/70">Code</dt>
          <dd>${error.code || 'Unbekannt'}</dd>
          <dt class="font-semibold text-base-content/70">Zeitpunkt</dt>
          <dd>${error.createdAt ? format(error.createdAt, 'dd.MM.yyyy HH:mm:ss') : ''}</dd>
          <dt class="font-semibold text-base-content/70">Methode</dt>
          <dd>${error.method}</dd>
          <dt class="font-semibold text-base-content/70">URL</dt>
          <dd class="break-all">${error.url}</dd>
          <dt class="font-semibold text-base-content/70">Meldung</dt>
          <dd>${error.message}</dd>
          ${
            error.data
              ? html`
                  <dt class="font-semibold text-base-content/70">Daten</dt>
                  <dd class="break-all">${error.data}</dd>
                `
              : nothing
          }
        </dl>
      </div>
    </li>
  `;
});

export const MicrosoftErrorsSection = stateful(() => {
  const { value: microsoftErrors, loading: errorsLoading } = useQuery(
    () => MicrosoftAPI.getRecentErrors(20)({ createdAt: 1, message: 1, code: 1, url: 1, method: 1, data: 1 }),
    [],
  );
  const recentErrors = (microsoftErrors as Maybe<MicrosoftErrorItem[]>) ?? [];

  if (errorsLoading) {
    return renderDashboardLoadingCard();
  }

  return html`
    <div class="card h-full">
      <div class="card-body gap-2">
        ${renderDashboardCardHeader('Microsoft-Fehler', 'icon-[material-symbols--bug-report-outline-rounded]', undefined, 'Die letzten 20 Fehler beim Abgleich mit Microsoft.')}
        ${
          recentErrors.length > 0
            ? html`<ul class="flex max-h-96 flex-col gap-1 overflow-y-auto">
                ${recentErrors.map(error => MicrosoftErrorRow({ error }))}
              </ul>`
            : EmptyState({
                title: 'Keine Fehler',
                description: 'Beim Abgleich mit Microsoft ist kein Fehler gespeichert.',
                icon: 'icon-[material-symbols--check-circle-outline-rounded]',
              })
        }
      </div>
    </div>
  `;
});
